import { useEffect, useState, useMemo } from 'preact/hooks';
import type { BookMeta } from '../audio/biblebrain.js';
import { bookmarks, removeBookmark } from '../state/bookmarks-store.js';
import { MemorizationPanel } from './MemorizationPanel.js';
import { heardChapters, chapterKey } from '../state/progress-store.js';
import { currentBible } from '../state/bible-store.js';
import { history } from '../state/history-store.js';
import { notes } from '../state/notes-store.js';
import {
  activePlan,
  todayChapters,
  ensurePlanData,
  planData,
} from '../state/reading-plans-store.js';
import { PlaylistChip } from './PlaylistPanel.js';

type Props = {
  books: BookMeta[];
  activeBook: string;
  activeChapter: number;
  onPick: (book: string, chapter: number) => void;
  onClose: () => void;
  onOpenPlans: () => void;
  onOpenPlaylist?: () => void;
};

export function ChapterPicker({ books, activeBook, activeChapter, onPick, onClose, onOpenPlans, onOpenPlaylist }: Props) {
  const [expandedBook, setExpandedBook] = useState<string>(activeBook);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const grouped = useMemo(() => {
    const ot: BookMeta[] = [];
    const nt: BookMeta[] = [];
    for (const b of books) (b.testament === 'OT' ? ot : nt).push(b);
    return { ot, nt };
  }, [books]);

  return (
    <div class="chapter-picker-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div class="chapter-picker" onClick={(e) => e.stopPropagation()}>
        <header class="chapter-picker-header">
          <span class="chapter-picker-eyebrow">Choose chapter</span>
          <button
            type="button"
            class="chapter-picker-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div class="chapter-picker-body">
          {onOpenPlaylist ? (
            <PlaylistChip onOpenPanel={() => { onClose(); onOpenPlaylist(); }} />
          ) : null}
          <DailyPickSection
            onPick={(book, ch) => {
              onPick(book, ch);
              onClose();
            }}
            onOpenPlans={() => {
              onClose();
              onOpenPlans();
            }}
          />
          <RecentSection
            onPick={(book, ch) => {
              onPick(book, ch);
              onClose();
            }}
          />
          <BookmarksSection
            onPick={(book, ch) => {
              onPick(book, ch);
              onClose();
            }}
          />
          <NotesSection
            onPick={(book, ch) => {
              onPick(book, ch);
              onClose();
            }}
          />
          {books.length === 0 ? (
            <p class="chapter-picker-empty">Loading books…</p>
          ) : (
            <>
              <Section
                label="Old Testament"
                books={grouped.ot}
                expandedBook={expandedBook}
                activeBook={activeBook}
                activeChapter={activeChapter}
                onExpand={setExpandedBook}
                onPick={(b, c) => {
                  onPick(b, c);
                  onClose();
                }}
              />
              <Section
                label="New Testament"
                books={grouped.nt}
                expandedBook={expandedBook}
                activeBook={activeBook}
                activeChapter={activeChapter}
                onExpand={setExpandedBook}
                onPick={(b, c) => {
                  onPick(b, c);
                  onClose();
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

type PickProps = { onPick: (book: string, chapter: number) => void };

function DailyPickSection({ onPick, onOpenPlans }: PickProps & { onOpenPlans: () => void }) {
  const active = activePlan.value;
  const chapters = todayChapters.value;

  useEffect(() => {
    if (active && !planData.value[active.id]) void ensurePlanData(active.id);
  }, [active?.id]);

  if (!active) {
    return (
      <section class="chapter-picker-section">
        <h2 class="chapter-picker-section-label">Today's reading</h2>
        <button type="button" class="chapter-picker-plan-prompt" onClick={onOpenPlans}>
          <span class="chapter-picker-plan-prompt-text">Select a reading plan to get today's reading</span>
          <span class="chapter-picker-plan-prompt-cta">Choose plan →</span>
        </button>
      </section>
    );
  }

  return (
    <section class="chapter-picker-section">
      <h2 class="chapter-picker-section-label">Today's reading</h2>
      <div class="chapter-picker-recent">
        {chapters.length === 0 ? (
          <span class="chapter-picker-plan-empty">Loading…</span>
        ) : (
          chapters.map((c) => (
            <button
              key={`${c.book}.${c.chapter}`}
              type="button"
              class="chapter-picker-recent-chip"
              onClick={() => onPick(c.book, c.chapter)}
            >
              {bookLabel(c.book)} {c.chapter}
            </button>
          ))
        )}
      </div>
    </section>
  );
}

function RecentSection({ onPick }: PickProps) {
  const list = history.value;
  if (list.length === 0) return null;
  return (
    <section class="chapter-picker-section">
      <h2 class="chapter-picker-section-label">Recent</h2>
      <div class="chapter-picker-recent">
        {list.slice(0, 8).map((h) => (
          <button
            key={`${h.bookId}.${h.chapter}.${h.visitedAt}`}
            type="button"
            class="chapter-picker-recent-chip"
            onClick={() => onPick(h.bookId, h.chapter)}
          >
            {bookLabel(h.bookId)} {h.chapter}
          </button>
        ))}
      </div>
    </section>
  );
}

function NotesSection({ onPick }: PickProps) {
  const list = Object.values(notes.value).sort((a, b) => b.updatedAt - a.updatedAt);
  if (list.length === 0) return null;
  return (
    <section class="chapter-picker-section">
      <h2 class="chapter-picker-section-label">Notes</h2>
      <ul class="chapter-picker-bookmark-list">
        {list.slice(0, 8).map((n) => {
          const [book, chStr] = n.ref.split('.');
          const chapter = Number(chStr);
          if (!book || !Number.isFinite(chapter)) return null;
          return (
            <li key={n.ref} class="chapter-picker-bookmark">
              <button
                type="button"
                class="chapter-picker-bookmark-main"
                onClick={() => onPick(book, chapter)}
              >
                <span class="chapter-picker-bookmark-ref">{n.ref.replace(/\./g, ' ')}</span>
                <span class="chapter-picker-bookmark-text">{n.text}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function bookLabel(bookId: string): string {
  const NAMES: Record<string, string> = {
    GEN: 'Gen', EXO: 'Ex', LEV: 'Lev', NUM: 'Num', DEU: 'Deut',
    JOS: 'Josh', JDG: 'Judg', RUT: 'Ruth', PSA: 'Ps', PRO: 'Prov',
    MAT: 'Matt', MRK: 'Mark', LUK: 'Luke', JHN: 'John', ACT: 'Acts',
    ROM: 'Rom', REV: 'Rev',
  };
  return NAMES[bookId] ?? bookId;
}

type BookmarksProps = { onPick: (book: string, chapter: number) => void };

function BookmarksSection({ onPick }: BookmarksProps) {
  const [memOpen, setMemOpen] = useState(false);
  const list = bookmarks.value;
  if (list.length === 0) return null;
  return (
    <section class="chapter-picker-section">
      <h2 class="chapter-picker-section-label">
        <span>Bookmarks</span>
        <button
          type="button"
          class="chapter-picker-section-action"
          onClick={() => setMemOpen(true)}
        >
          Memorize ↗
        </button>
      </h2>
      {memOpen ? <MemorizationPanel onClose={() => setMemOpen(false)} /> : null}
      <ul class="chapter-picker-bookmark-list">
        {list.map((b) => (
          <li key={b.ref} class="chapter-picker-bookmark">
            <button
              type="button"
              class="chapter-picker-bookmark-main"
              onClick={() => onPick(b.bookId, b.chapter)}
            >
              <span class="chapter-picker-bookmark-ref">
                {b.bookName} {b.chapter}:{b.verseNumber}
              </span>
              <span class="chapter-picker-bookmark-text">{b.text}</span>
            </button>
            <button
              type="button"
              class="chapter-picker-bookmark-remove"
              aria-label={`Remove bookmark ${b.bookName} ${b.chapter}:${b.verseNumber}`}
              onClick={(e) => {
                e.stopPropagation();
                removeBookmark(b.ref);
              }}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

type SectionProps = {
  label: string;
  books: BookMeta[];
  expandedBook: string;
  activeBook: string;
  activeChapter: number;
  onExpand: (book: string) => void;
  onPick: (book: string, chapter: number) => void;
};

function Section({
  label,
  books,
  expandedBook,
  activeBook,
  activeChapter,
  onExpand,
  onPick,
}: SectionProps) {
  if (books.length === 0) return null;
  return (
    <section class="chapter-picker-section">
      <h2 class="chapter-picker-section-label">{label}</h2>
      {books.map((book) => {
        const open = book.bookId === expandedBook;
        return (
          <section key={book.bookId} class={`chapter-picker-book${open ? ' is-open' : ''}`}>
            <button
              type="button"
              class="chapter-picker-book-name"
              aria-expanded={open}
              onClick={() => onExpand(open ? '' : book.bookId)}
            >
              <span>{book.bookName}</span>
              <span class="chapter-picker-book-meta">{book.chapters.length} ch</span>
            </button>
            {open ? (
              <div class="chapter-picker-grid">
                {book.chapters.map((ch) => {
                  const isActive = book.bookId === activeBook && ch === activeChapter;
                  const isHeard =
                    heardChapters.value[
                      chapterKey(currentBible.value.bibleId, book.bookId, ch)
                    ] === true;
                  return (
                    <button
                      key={ch}
                      type="button"
                      class={`chapter-picker-num${isActive ? ' active' : ''}${isHeard ? ' is-heard' : ''}`}
                      onClick={() => onPick(book.bookId, ch)}
                    >
                      {ch}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </section>
        );
      })}
    </section>
  );
}
